import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  AddressSelectFormComponent,
  AddressSelectFormConfig,
  AddressSelectFormModel,
  AddressSelectFormService,
  GkPostalCode,
  LawPersonAddressControlName,
  LawPersonControlName,
  PetentData,
  Place,
  PostOfficeModalComponent,
  PzService,
  ReceivingTypesItemForms,
  ShippingForm,
  ShippingService,
  Street,
} from '@gk/gk-modules';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs';
import { DocumentationFormType } from '../base-new-request/base-new-request.model';
import { Shipping, ShippingFormControlName } from './shipping-form.model';

@Component({
  selector: 'app-shipping-form',
  templateUrl: './shipping-form.component.html',
  styleUrls: ['./shipping-form.component.scss'],
  standalone: false,
})
export class ShippingFormComponent implements OnInit, OnChanges, OnDestroy {
  private isAlive = true;
  @Input() shippingFormGroup: UntypedFormGroup;
  @Input() portalId: number | string;
  @Input() removeDisabledControls: boolean;
  @Input() idPrefix = '';
  @Input() primitivePostalCode: boolean;
  @Input() wasFormValidated: boolean;
  @Input() documentationFormRadioFormControl: AbstractControl;
  @Input() set addressSelectFormConfig(value: AddressSelectFormConfig) {
    this.addressSelectFormService.emitConfig(value);
  }
  private addressSelectFormGroup$ =
    new BehaviorSubject<AddressSelectFormModel | null>(null);
  private _addressSelectFormComponent: AddressSelectFormComponent | undefined;
  @ViewChild(AddressSelectFormComponent)
  set addressSelectFormComponent(ref: AddressSelectFormComponent | undefined) {
    this._addressSelectFormComponent = ref;
    if (ref) {
      this.addressSelectFormGroup$
        .pipe(
          take(1),
          filter((group) => !!group),
        )
        .subscribe((addressSelectFormGroup) => {
          ref.setAddressSelectFormGroupValue(addressSelectFormGroup);
        });
    }
  }
  shippingFormControlName = ShippingFormControlName;
  lawPersonAddressControlName = LawPersonAddressControlName;
  documentationFormType = DocumentationFormType;
  shippingTypes: ShippingForm[];
  customPostOfficeModalHeaderInfo: string;
  postOfficeModalHeaderInfoTranslation: string;
  @ViewChild(PostOfficeModalComponent)
  postOfficeModalComponent: PostOfficeModalComponent;
  shippingFormType: ShippingForm;
  addressSelectFormOptions = {
    formState: {
      disableGroup: false,
    },
  };

  constructor(
    private shippingService: ShippingService,
    private translateService: TranslateService,
    private pzService: PzService,
    private addressSelectFormService: AddressSelectFormService,
  ) {}

  ngOnInit(): void {
    this.copyApplicantDataFromPz().subscribe();
    this.setDefaultShippingForm();
    this.setProperShippingDataInputs();
    this.fetchPostOfficeModalHeaderInfoTranslation();
    this.adjustFormToIsOtherAddressValueOnChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentationFormRadioFormControl']) {
      this.setDefaultShippingFormOnDocumentationFormChange();
    }
  }

  setDefaultShippingFormOnDocumentationFormChange(): void {
    this.documentationFormRadioFormControl.valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => {
        this.setDefaultShippingForm();
      });
  }

  adjustFormToIsOtherAddressValueOnChange(): void {
    const addressFormGroup = this.getAddressFormGroup();
    this.getIsOtherAddressFormControl()
      .valueChanges.pipe(
        takeWhile(() => this.isAlive),
        switchMap((isOtherAddressValue) =>
          this.adjustShippingFormValueToOtherAddressValue(isOtherAddressValue),
        ),
      )
      .subscribe((isOtherAddressValue) => {
        if (!isOtherAddressValue) {
          this.getNameSurnameFormControl().disable();
          if (addressFormGroup) {
            addressFormGroup.disable({ emitEvent: false });
          }
          this.addressSelectFormOptions.formState.disableGroup = true;
        } else {
          this.getNameSurnameFormControl().enable();
          if (addressFormGroup) {
            addressFormGroup.enable();
          }
          this.addressSelectFormOptions.formState.disableGroup = false;
        }
      });
  }

  adjustShippingFormValueToOtherAddressValue(
    isOtherAddressValue: boolean,
  ): Observable<boolean> {
    if (isOtherAddressValue) {
      return of(isOtherAddressValue);
    } else {
      return this.copyApplicantDataFromPz().pipe(
        takeWhile(() => this.isAlive),
        map(() => isOtherAddressValue),
      );
    }
  }

  copyApplicantDataFromPz(): Observable<PetentData> {
    return this.pzService.getPetentData().pipe(
      takeWhile(() => this.isAlive),
      tap((petentData) => {
        this.getNameSurnameFormControl()?.setValue(
          `${petentData.firstname} ${petentData.lastname}`,
        );
        this.getAddressFormGroup()?.patchValue({
          [LawPersonAddressControlName.Place]: new Place(
            petentData.placeId,
            petentData.place,
            true,
          ),
          [LawPersonAddressControlName.Street]: new Street(
            petentData.streetId,
            petentData.fullStreet,
          ),
          [LawPersonAddressControlName.PostalCode]: new GkPostalCode(
            petentData.postalCodeId,
            petentData.postalCode,
          ),
          [LawPersonAddressControlName.BuildingNumber]:
            petentData.buildingNumber,
          [LawPersonAddressControlName.LocalNumber]: petentData.localNumber,
          [LawPersonAddressControlName.StreetAbsence]: !petentData.street,
        });
      }),
    );
  }

  setDefaultShippingForm(): void {
    this.fetchShippingForms()
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => {
        if (!this.shippingTypes?.length) {
          return;
        }
        this.getShippingFormTypeRadioFormControl().setValue(
          this.shippingTypes[0].id,
        );
      });
  }

  setShippingForm(shippingForm: Shipping): void {
    this.fetchShippingForms()
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => {
        const shippingFormTypeFoundInAvailableTypes = this.shippingTypes?.find(
          (shippingType) => shippingType.id === shippingForm.shippingForm,
        );
        this.getShippingFormTypeRadioFormControl().setValue(
          shippingFormTypeFoundInAvailableTypes
            ? shippingFormTypeFoundInAvailableTypes.id
            : this.shippingTypes?.[0]?.id || null,
        );
        if (shippingForm.addressSelect) {
          this.getIsOtherAddressFormControl().setValue(true);
          this.setAddressSelectFormGroupValue(shippingForm.addressSelect);
        }
      });
  }

  setAddressSelectFormGroupValue(
    addressSelectFormGroup: AddressSelectFormModel,
  ): void {
    if (!addressSelectFormGroup) {
      return;
    }
    if (this._addressSelectFormComponent) {
      this._addressSelectFormComponent.setAddressSelectFormGroupValue(
        addressSelectFormGroup,
      );
    } else {
      this.addressSelectFormGroup$.next(addressSelectFormGroup);
    }
  }

  setProperShippingDataInputs(): void {
    this.disableShippingDataFormGroups();
    this.getShippingFormTypeRadioFormControl()
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .subscribe((shippingFormValue: number) => {
        const isOtherAddressValue = this.getIsOtherAddressFormControl().value;
        this.disableShippingDataFormGroups();
        this.shippingFormType = this.shippingTypes.find(
          (shippingType) => shippingType.id === shippingFormValue,
        );
        if (
          shippingFormValue &&
          this.shippingFormType.postalDataRequired &&
          isOtherAddressValue
        ) {
          this.getNameSurnameFormControl().enable();
          this.getAddressFormGroup().enable();

          return;
        }
        if (shippingFormValue && this.shippingFormType.emailRequired) {
          this.getEmailFormControl().enable();

          return;
        }
        if (shippingFormValue && this.shippingFormType.ftpAddressRequired) {
          this.getFtpAddressFormControl().enable();

          return;
        }
      });
  }

  fetchShippingForms(): Observable<ShippingForm[]> {
    if (!this.portalId) {
      return of([]);
    }

    return this.shippingService
      .getShippingForms(
        this.portalId,
        this.documentationFormRadioFormControl?.value ===
          DocumentationFormType.ElectronicForm
          ? ReceivingTypesItemForms.Electronic
          : ReceivingTypesItemForms.Printed,
      )
      .pipe(tap((data) => (this.shippingTypes = data)));
  }

  fetchPostOfficeModalHeaderInfoTranslation(): void {
    this.translateService
      .get('SHIPPING_CUSTOM_POST_OFFICE_MODAL_HEADER_INFO')
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(
        (postOfficeModalHeaderInfoTranslation) =>
          (this.postOfficeModalHeaderInfoTranslation =
            postOfficeModalHeaderInfoTranslation),
      );
  }

  disableShippingDataFormGroups(): void {
    this.getNameSurnameFormControl().disable();
    this.getEmailFormControl().disable();
    this.getFtpAddressFormControl().disable();
    this.getAddressFormGroup().disable({ emitEvent: false });
  }

  getIsOtherAddressFormControl(): UntypedFormControl {
    return this.shippingFormGroup.get(
      this.shippingFormControlName.IsOtherAddress,
    ) as UntypedFormControl;
  }

  getNameSurnameFormControl(): UntypedFormControl {
    return this.shippingFormGroup.get(
      this.shippingFormControlName.NameSurname,
    ) as UntypedFormControl;
  }

  getEmailFormControl(): UntypedFormControl {
    return this.shippingFormGroup.get(
      this.shippingFormControlName.Email,
    ) as UntypedFormControl;
  }

  getFtpAddressFormControl(): UntypedFormControl {
    return this.shippingFormGroup.get(
      this.shippingFormControlName.FtpAddress,
    ) as UntypedFormControl;
  }

  getAddressFormGroup(): UntypedFormGroup {
    return this.shippingFormGroup.get(
      LawPersonControlName.Address,
    ) as UntypedFormGroup;
  }

  getAddressSelectFormGroup(): UntypedFormGroup {
    return this.shippingFormGroup.get(
      LawPersonControlName.AddressSelect,
    ) as UntypedFormGroup;
  }

  getShippingFormTypeRadioFormControl(): FormControl<number> {
    return this.shippingFormGroup.get(
      this.shippingFormControlName.ShippingForm,
    ) as FormControl<number>;
  }

  askForPostOfficeWhenPostalCodeIsNotFromDictionary(
    customPostOfficeModalHeaderInfo = this.postOfficeModalHeaderInfoTranslation,
  ): Observable<boolean> {
    return this.postOfficeModalComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(
      customPostOfficeModalHeaderInfo,
    );
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
